const MONTHS = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

class CountdownBanner extends HTMLElement {
  // ── static helpers ──────────────────────────────────────────────────────

  static parseDate(dateStr, tz) {
    if (!dateStr) return null;
    const text = String(dateStr).trim();
    if (!text) return null;
    const offset = tz !== undefined && tz !== null && String(tz).trim() !== '' ? parseFloat(tz) : 0;
    const match = text.match(/^([A-Za-z]+)\s+(\d{1,2}),\s+(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/);
    if (match) {
      const month = MONTHS[match[1].toLowerCase()];
      if (month !== undefined) {
        return Date.UTC(+match[3], month, +match[2], +match[4] - offset, +match[5], +match[6]);
      }
    }
    const ms = Date.parse(text);
    return Number.isNaN(ms) ? null : ms;
  }

  static extensionEndAt(config, endAt) {
    if (!config.surpriseExtension?.enabled || !endAt) return null;
    const mins = Number(config.surpriseExtension.durationMinutes || 0);
    return mins > 0 ? endAt + mins * 60000 : null;
  }

  static surpriseOn(config) {
    return Boolean(config.surpriseExtension?.enabled);
  }

  static isDesignPreview(c) {
    if (!c?.designMode || !c?.startAt || !c?.endAt) return false;
    const now = Date.now();
    if (now < c.startAt) return true;
    if (now >= c.startAt && now < c.endAt) return true;
    return Boolean(
      CountdownBanner.surpriseOn(c) && c.extensionEndAt && now >= c.endAt && now < c.extensionEndAt,
    );
  }

  static refreshConfig(raw) {
    const c = Object.assign({}, raw || {});
    const serverActive = Boolean(raw?.active);
    const serverExtended = Boolean(raw?.initiallyExtended);

    c.startAt = CountdownBanner.parseDate(c.startDateStr, c.startTimezone);
    c.endAt = CountdownBanner.parseDate(c.endDateStr, c.endTimezone);
    c.extensionEndAt = CountdownBanner.extensionEndAt(c, c.endAt);
    c.active = false;
    c.initiallyExtended = false;

    if (!c.startAt || !c.endAt) {
      c.active = serverActive;
      c.initiallyExtended = serverExtended;
      return c;
    }

    const now = Date.now();
    if (now >= c.startAt && now < c.endAt) {
      c.active = true;
    } else if (
      CountdownBanner.surpriseOn(c) &&
      c.extensionEndAt &&
      now >= c.endAt &&
      now < c.extensionEndAt
    ) {
      c.active = true;
      c.initiallyExtended = true;
    }

    if (c.designMode && c.startAt && c.endAt) {
      if (now < c.startAt) c.active = true;
      else if (now >= c.startAt && now < c.endAt) c.active = true;
      else if (
        CountdownBanner.surpriseOn(c) &&
        c.extensionEndAt &&
        now >= c.endAt &&
        now < c.extensionEndAt
      ) {
        c.active = true;
        c.initiallyExtended = true;
      }
    } else if (!c.active && serverActive) {
      c.active = true;
      c.initiallyExtended = serverExtended;
    }

    return c;
  }

  static getConfig() {
    return CountdownBanner.refreshConfig(window.theme?.countdown);
  }

  static placementAllowsInline(c) {
    const p = c?.pdpResultsPlacement || 'top_only';
    return p === 'inline_only' || p === 'top_and_inline';
  }

  static placementHidesTopOnResults(c) {
    return (c?.pdpResultsPlacement || 'top_only') === 'inline_only';
  }

  static canShowBanner(c, opts) {
    if (!c) return false;
    if (!c.active && !CountdownBanner.isDesignPreview(c)) return false;

    const variant = opts.variant || 'global';
    const surface = opts.surface || '';

    if (variant === 'inline') {
      if (surface === 'pdp' || surface === 'quiz') return CountdownBanner.placementAllowsInline(c);
      return false;
    }

    if (surface === 'quiz-top') return opts.quizTopAllowed !== false;

    if (!c.enableGlobal) return false;
    if (c.isQuiz) return false;
    return true;
  }

  static syncOffset() {
    if (typeof window.syncHeaderOffset === 'function') {
      window.syncHeaderOffset();
    }
  }

  static mountAll() {
    const c = CountdownBanner.getConfig();
    if (!c?.active && !CountdownBanner.isDesignPreview(c)) return;

    document.querySelectorAll('[data-countdown-mount]').forEach(function (mount) {
      if (mount.dataset.mounted === 'true') return;

      const variant = mount.dataset.countdownMount || 'inline';
      const surface = mount.dataset.surface || '';

      if (!CountdownBanner.canShowBanner(c, { variant, surface, quizTopAllowed: true })) return;

      const template = document.getElementById('countdown-banner-template-' + variant);
      if (!template) return;

      const node = template.content.cloneNode(true);
      const banner = node.querySelector('countdown-banner');
      if (!banner) return;
      if (surface) banner.dataset.surface = surface;

      mount.appendChild(node);
      mount.dataset.mounted = 'true';
    });

    CountdownBanner.syncOffset();
  }

  // ── instance lifecycle ───────────────────────────────────────────────────

  connectedCallback() {
    this.variant = this.dataset.variant || 'global';
    this.surface = this.dataset.surface || '';
    this.timerEl = this.querySelector('.countdown-banner__timer');
    this.textEl = this.querySelector('.countdown-banner__text');
    this.iconEl = this.querySelector('.countdown-banner__icon');
    this.inExtension = this.dataset.extended === 'true';
    this.tickTimer = null;
    this.extensionEndMs = null;

    this.reload();

    if (!this.timerEl || !this.visible()) {
      this.removeBanner();
      return;
    }

    if (this.config.initiallyExtended) this.setExtension();
    this.tick();
  }

  disconnectedCallback() {
    if (this.tickTimer) clearTimeout(this.tickTimer);
  }

  reload() {
    this.config = CountdownBanner.getConfig();
  }

  visible() {
    return CountdownBanner.canShowBanner(this.config, {
      variant: this.variant,
      surface: this.surface,
      quizTopAllowed: this.dataset.quizTopAllowed !== 'false',
    });
  }

  setExtension() {
    const c = this.config;
    this.inExtension = true;
    this.extensionEndMs = CountdownBanner.extensionEndAt(c, c.endAt);
    if (!this.extensionEndMs) return;

    const schemeId = c.extendedColorScheme || c.colorScheme;
    this.className = this.className.replace(/color-[^\s]+/g, '').trim();
    this.classList.add('scheme', 'color-' + schemeId, 'is-extended');
    this.textEl.textContent = (c.extensionText && String(c.extensionText).trim()) || c.text || '';

    const isInline = this.variant === 'inline';
    const extensionIcon = isInline
      ? c.inlineExtensionIcon || c.extensionIcon
      : c.extensionIcon;
    const fallbackIcon = isInline ? c.inlineIcon || c.icon : c.icon;

    if (this.iconEl && extensionIcon?.src) {
      this.iconEl.src = extensionIcon.src;
      if (extensionIcon.width) this.iconEl.width = extensionIcon.width;
      if (extensionIcon.height) this.iconEl.height = extensionIcon.height;
    } else if (this.iconEl && fallbackIcon?.src) {
      this.iconEl.src = fallbackIcon.src;
    }
    const bg = c.extendedBackgroundImage || c.backgroundImage;
    if (bg) this.style.setProperty('--countdown-banner-bg-image', 'url(' + bg + ')');
    this.dataset.extended = 'true';
  }

  tick() {
    this.reload();
    const c = this.config;

    if (!this.visible() || !this.timerEl || !c.endAt) {
      this.removeBanner();
      return;
    }

    const now = Date.now();
    if (c.startAt && now < c.startAt && !c.designMode) {
      this.removeBanner();
      return;
    }

    if (!this.inExtension && now >= c.endAt) {
      if (CountdownBanner.surpriseOn(c) && c.extensionEndAt && now < c.extensionEndAt) {
        this.setExtension();
      } else {
        this.removeBanner();
        return;
      }
    }

    const endMs = this.inExtension ? this.extensionEndMs || c.extensionEndAt : c.endAt;
    const distance = endMs - now;

    if (distance <= 0) {
      this.removeBanner();
      return;
    }

    this.renderTimer(distance);
    this.tickTimer = setTimeout(() => this.tick(), 1000);
  }

  renderTimer(distance) {
    const c = this.config;
    const labels = c.labels || {};
    let total = Math.max(0, Math.floor(distance / 1000));
    let days = Math.floor(total / 86400);
    total %= 86400;
    let hours = Math.floor(total / 3600);
    total %= 3600;
    const mins = Math.floor(total / 60);
    const secs = total % 60;

    if (!c.showDays) {
      hours += days * 24;
      days = 0;
    }

    const units = [];
    if (c.showDays) units.push({ value: days, label: labels.days || 'Day' });
    units.push(
      { value: hours, label: labels.hours || 'Hrs' },
      { value: mins, label: labels.mins || 'Mins' },
      { value: secs, label: labels.secs || 'Secs' },
    );

    this.timerEl.innerHTML =
      '<div class="countdown-banner__timer-digits-row">' +
      units
        .map((unit, index) => {
          const sep =
            index < units.length - 1
              ? '<span class="countdown-banner__sep" aria-hidden="true">:</span>'
              : '';
          return (
            '<div class="countdown-banner__unit">' +
            '<div class="countdown-banner__digits">' +
            String(unit.value).padStart(2, '0') +
            '</div></div>' +
            sep
          );
        })
        .join('') +
      '</div>' +
      '<div class="countdown-banner__timer-labels-row">' +
      units
        .map((unit, index) => {
          const sep =
            index < units.length - 1
              ? '<span class="countdown-banner__sep countdown-banner__sep--spacer" aria-hidden="true">:</span>'
              : '';
          return (
            '<div class="countdown-banner__unit countdown-banner__unit--label">' +
            '<div class="countdown-banner__label">' +
            unit.label +
            '</div></div>' +
            sep
          );
        })
        .join('') +
      '</div>';
  }

  removeBanner() {
    const section = this.closest('.type_Countdown_Banner');
    this.remove();
    if (section && !section.querySelector('countdown-banner')) {
      section.classList.add('countdown-banner-section--collapsed');
    }
    CountdownBanner.syncOffset();
  }
}

customElements.define('countdown-banner', CountdownBanner);

window.refreshCountdownConfig = CountdownBanner.refreshConfig.bind(CountdownBanner);
window.syncCountdownBannerOffset = CountdownBanner.syncOffset.bind(CountdownBanner);
window.mountCountdownBanners = CountdownBanner.mountAll.bind(CountdownBanner);

document.addEventListener('DOMContentLoaded', function () {
  if (CountdownBanner.getConfig()?.isQuiz) {
    document
      .querySelectorAll('.top-announcement-stack .type_Countdown_Banner countdown-banner')
      .forEach(function (el) {
        el.remove();
      });
  }

  CountdownBanner.mountAll();

  const announcementSection = document.querySelector('.type_Announcement_Bar');
  if (announcementSection && typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(CountdownBanner.syncOffset).observe(announcementSection);
  }
});

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (evt) {
    const sectionId = evt.detail && evt.detail.sectionId;
    const root = sectionId ? document.getElementById('shopify-section-' + sectionId) : null;

    if (root) {
      root.querySelectorAll('[data-countdown-mount]').forEach(function (m) {
        m.dataset.mounted = 'false';
        m.replaceChildren();
      });
    }

    CountdownBanner.mountAll();
    CountdownBanner.syncOffset();
  });
}
